// src/pages/Connector/Instagram/ListInstagramPosts.js
import React, { useState, useEffect, useCallback } from "react";
import {useParams} from 'react-router-dom';

import axiosInstance from "../../../utils/axiosInstance";
import DataTable from "react-data-table-component";
import { Tooltip } from '@mui/material';
import CommentAnalysisModal from "./CommentAnalysisModal";

export default function ListInstagramPosts({ refreshTrigger, permission }) {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [isSyncing, setIsSyncing] = useState(false);
    const [selectedPost, setSelectedPost] = useState(null);
    const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    
    const { chatbotId } = useParams();
    const params = chatbotId ? { chatbot_id: chatbotId } : {};

    const fetchPosts = useCallback(async (page = currentPage, pageSize = perPage, search = searchText) => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await axiosInstance.get(`/api/instagram/posts`, {
                params: {
                    ...params,
                    page: page,
                    per_page: pageSize,
                    search: search
                }
            });
            
            if (response.data.success) {
                setPosts(response.data.data);
                setTotalRows(response.data.pagination.total);
            } else {
                setError(response.data.message || "Failed to fetch posts");
            }
        } catch (err) {
            console.error("Error fetching Instagram posts:", err);
            setError(err.response?.data?.message || "Error fetching posts.");
        } finally {
            setLoading(false);
        }
    }, [currentPage, perPage, searchText]);

    useEffect(() => {
        fetchPosts();
    }, [refreshTrigger]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchText !== undefined) {
                setCurrentPage(1);
                fetchPosts(1, perPage, searchText);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchText]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchPosts(page, perPage, searchText);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
        fetchPosts(page, newPerPage, searchText);
    };

    const handleSyncPosts = async () => {
        setIsSyncing(true);
        setError(null);
        
        try {
            const response = await axiosInstance.post('/api/instagram/posts/sync');
            if (response.data.success) {
                setError(`Successfully synced! ${response.data.message}`);
                fetchPosts();
            } else {
                setError(response.data.message || "Failed to sync posts");
            }
        } catch (err) {
            console.error("Error syncing posts:", err);
            setError(err.response?.data?.message || "Error syncing posts.");
        } finally {
            setIsSyncing(false);
        }
    };

    const handleAnalyzeComments = (post) => {
        setSelectedPost(post);
        setIsAnalysisModalOpen(true);
    };

    const handleDeletePost = async (postId, caption) => {
        if (!window.confirm(`Are you sure you want to delete this post? This action cannot be undone.`)) {
            return;
        }

        setIsDeleting(true);
        setError(null);
        
        try {
            await axiosInstance.delete(`/api/instagram/posts/${postId}`);
            setError("Post deleted successfully.");
            fetchPosts();
        } catch (err) {
            console.error(`Error deleting post ${postId}:`, err);
            setError(err.response?.data?.message || `Error deleting post.`);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleModalClose = () => {
        setIsAnalysisModalOpen(false);
        setSelectedPost(null);
        fetchPosts(); // Refresh to show updated analysis status
    };

    const getMediaTypeIcon = (mediaType) => {
        switch(mediaType) {
            case 'IMAGE':
                return <i className="fa fa-image text-primary"></i>;
            case 'VIDEO':
                return <i className="fa fa-video-camera text-danger"></i>;
            case 'CAROUSEL_ALBUM':
                return <i className="fa fa-th text-info"></i>;
            default:
                return <i className="fa fa-file text-secondary"></i>;
        }
    };

    const columns = [
        {
            name: "Media",
            width: '80px',
            cell: (row) => (
                <div style={{ padding: '5px' }}>
                    {row.thumbnail_url || row.media_url ? (
                        <img 
                            src={row.thumbnail_url || row.media_url} 
                            alt="Post" 
                            style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                        />
                    ) : (
                        getMediaTypeIcon(row.media_type)
                    )}
                </div>
            )
        },
        {
            name: "Caption",
            selector: (row) => row.caption,
            sortable: true,
            grow: 2,
            wrap: true,
            cell: (row) => (
                <div>
                    <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                        {row.caption ? (row.caption.length > 100 ? row.caption.substring(0, 100) + '...' : row.caption) : 'No caption'}
                    </div>
                    {row.post_url && (
                        <a href={row.post_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.85em', color: '#0066cc' }}>
                            <i className="fa fa-external-link me-1"></i>View on Instagram
                        </a>
                    )}
                </div>
            )
        },
        {
            name: "Engagement",
            width: '130px',
            cell: (row) => (
                <div style={{ fontSize: '0.9em' }}>
                    <div><i className="fa fa-heart text-danger me-1"></i>{row.like_count || 0}</div>
                    <div><i className="fa fa-comment text-primary me-1"></i>{row.comments_count || 0}</div>
                </div>
            )
        },
        {
            name: "Status",
            width: '120px',
            cell: (row) => (
                <div>
                    {row.is_analyzed && <span className="badge bg-success me-1">Analyzed</span>}
                    {row.is_trained && <span className="badge bg-primary">Trained</span>}
                    {!row.is_analyzed && <span className="badge bg-secondary">Not Analyzed</span>}
                </div>
            )
        },
        {
            name: "Actions",
            width: '180px',
            cell: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {permission.canModify && (
                        <Tooltip title="Analyze Comments" arrow>
                            <button 
                                className="action-icon" 
                                onClick={() => handleAnalyzeComments(row)}
                                disabled={loading || isDeleting || row.comments_count === 0}
                            >
                                <i className="fa fa-bar-chart"></i>
                            </button>
                        </Tooltip>
                    )}
                    {permission.canDelete && (
                        <Tooltip title="Delete Post" arrow>
                            <button 
                                className="action-icon delete" 
                                onClick={() => handleDeletePost(row.id, row.caption)}
                                disabled={loading || isDeleting}
                            >
                                <i className="fa fa-trash-o"></i>
                            </button>
                        </Tooltip>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    const ExpandedComponent = ({ data }) => (
        <div style={{ padding: '15px', borderLeft: '3px solid #e1306c', backgroundColor: '#f8f9fa', fontSize: '0.9em' }}>
            <p><strong>Post ID:</strong> {data.instagram_post_id}</p>
            <p><strong>Media Type:</strong> {data.media_type}</p>
            <p><strong>Posted:</strong> {data.timestamp ? new Date(data.timestamp).toLocaleString() : 'N/A'}</p>
            <p><strong>Likes:</strong> {data.like_count || 0}</p>
            <p><strong>Comments:</strong> {data.comments_count || 0}</p>
            <p><strong>Last Synced:</strong> {data.last_sync_date ? new Date(data.last_sync_date).toLocaleString() : 'Never'}</p>
            {data.caption && <p><strong>Full Caption:</strong> {data.caption}</p>}
        </div>
    );

    return (
        <div className="mt-3">
            <div className="row mb-3">
                <div className="col-md-6">
                    {permission.canModify && (
                        <button
                            className="btn btn-primary"
                            onClick={handleSyncPosts}
                            disabled={isSyncing || loading}
                        >
                            {isSyncing ? (
                                <><span className="spinner-border spinner-border-sm me-2"></span>Syncing...</>
                            ) : (
                                <><i className="fa fa-refresh me-2"></i>Sync Posts</>
                            )}
                        </button>
                    )}
                </div>
                <div className="col-md-6">
                    <input
                        type="text"
                        placeholder="Search by caption..."
                        className="form-control"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </div>
            </div>

            {error && (
                <div className={`alert ${error.toLowerCase().includes("error") || error.toLowerCase().includes("failed") ? "alert-danger" : "alert-success"} my-2 alert-dismissible fade show`} role="alert">
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close"></button>
                </div>
            )}

            <DataTable
                columns={columns}
                data={posts}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                paginationPerPage={perPage}
                paginationRowsPerPageOptions={[10, 25, 50]}
                responsive
                highlightOnHover
                pointerOnHover
                expandableRows
                expandableRowsComponent={ExpandedComponent}
            />

            {/* Comment Analysis Modal */}
            {isAnalysisModalOpen && selectedPost && (
                <CommentAnalysisModal
                    isOpen={isAnalysisModalOpen}
                    onClose={handleModalClose}
                    post={selectedPost}
                    permission={permission}
                />
            )}

            <style jsx global>{`
                .action-icon { 
                    background: none; 
                    border: none; 
                    padding: 5px; 
                    cursor: pointer; 
                    color: #0d6efd; 
                    font-size: 1.1em; 
                }
                .action-icon.delete { color: #dc3545; }
                .action-icon:hover { opacity: 0.7; }
                .action-icon:disabled { opacity: 0.5; cursor: not-allowed; }
            `}</style>
        </div>
    );
}